
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FavoriteButton } from "./FavoriteButton";
import { MessageDialog } from "./MessageDialog";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Euro, 
  Calendar,
  MessageCircle,
  Star,
  Users,
  Timer,
  Award,
  Zap
} from "lucide-react";
import { type Service } from "./types";

interface ServiceInfoSectionProps {
  service: Service;
  onContactClick: () => void;
  onAppointmentClick: () => void;
}

export const ServiceInfoSection = ({ 
  service, 
  onContactClick, 
  onAppointmentClick 
}: ServiceInfoSectionProps) => {
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);

  return (
    <div className="space-y-6">
      {/* En-tête du service */}
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {service.service_name}
          </h1>
          {service.category && (
            <Badge variant="secondary" className="mb-4">
              {service.category}
            </Badge>
          )}
        </div>
        <FavoriteButton serviceId={service.id} />
      </div>

      {/* Prix et durée */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Euro className="h-5 w-5 text-green-600" />
              <span className="text-2xl font-bold text-green-600">
                {service.price}€
              </span>
              <span className="text-gray-500">
                {service.currency === 'EUR' ? '€' : service.currency}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="h-4 w-4" />
              <span>{service.duration} min</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Professionnel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Professionnel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage 
                src={service.profiles?.profile_photo} 
                alt={service.profiles?.full_name || "Professionnel"} 
              />
              <AvatarFallback>
                {service.profiles?.full_name?.charAt(0) || "P"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">
                {service.profiles?.full_name || "Professionnel"}
              </h3>
              <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                {service.profiles?.rating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span>{service.profiles.rating}/5</span>
                    {service.profiles.total_reviews && (
                      <span>({service.profiles.total_reviews} avis)</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Statistiques du professionnel */}
          <div className="grid grid-cols-2 gap-4 mt-4">
            {service.profiles?.response_rate && (
              <div className="flex items-center gap-2 text-sm">
                <Zap className="h-4 w-4 text-blue-500" />
                <span>{service.profiles.response_rate}% de réponse</span>
              </div>
            )}
            {service.profiles?.response_time && (
              <div className="flex items-center gap-2 text-sm">
                <Timer className="h-4 w-4 text-green-500" />
                <span>Répond en {service.profiles.response_time}h</span>
              </div>
            )}
          </div>

          {/* À propos */}
          {service.profiles?.about_me && (
            <div className="pt-4 border-t">
              <h4 className="font-medium text-sm text-gray-700 mb-2">À propos</h4>
              <p className="text-sm text-gray-600">{service.profiles.about_me}</p>
            </div>
          )}

          {/* Expérience et formation */}
          <div className="grid grid-cols-1 gap-4 pt-4 border-t">
            {service.profiles?.experience && (
              <div>
                <h4 className="font-medium text-sm text-gray-700 mb-1 flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Expérience
                </h4>
                <p className="text-sm text-gray-600">{service.profiles.experience}</p>
              </div>
            )}
            {service.profiles?.education && (
              <div>
                <h4 className="font-medium text-sm text-gray-700 mb-1">Formation</h4>
                <p className="text-sm text-gray-600">{service.profiles.education}</p>
              </div>
            )}
          </div>

          {/* Langues et certifications */}
          {(service.profiles?.languages || service.profiles?.certifications) && (
            <div className="pt-4 border-t space-y-3">
              {service.profiles?.languages && (
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Langues</h4>
                  <div className="flex flex-wrap gap-1">
                    {service.profiles.languages.map((language, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {language}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {service.profiles?.certifications && (
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Certifications</h4>
                  <div className="flex flex-wrap gap-1">
                    {service.profiles.certifications.map((cert, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {cert}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Description */}
      {service.description && (
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed">{service.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Informations de contact */}
      <Card>
        <CardHeader>
          <CardTitle>Informations pratiques</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {service.address && (
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
              <span className="text-gray-700">{service.address}</span>
            </div>
          )}
          {service.phone && (
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-gray-400" />
              <span className="text-gray-700">{service.phone}</span>
            </div>
          )}
          {service.email && (
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-gray-400" />
              <span className="text-gray-700">{service.email}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Boutons d'action */}
      <div className="grid grid-cols-1 gap-3">
        <Button 
          onClick={onAppointmentClick}
          size="lg"
          className="w-full"
        >
          <Calendar className="h-5 w-5 mr-2" />
          Prendre rendez-vous
        </Button>
        
        <div className="grid grid-cols-2 gap-3">
          <Button 
            variant="outline" 
            onClick={onContactClick}
            size="lg"
          >
            <Mail className="h-5 w-5 mr-2" />
            Contact
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setIsMessageDialogOpen(true)}
            size="lg"
          >
            <MessageCircle className="h-5 w-5 mr-2" />
            Message
          </Button>
        </div>
      </div>

      {/* Dialog de message */}
      <MessageDialog
        open={isMessageDialogOpen}
        onOpenChange={setIsMessageDialogOpen}
        professionalId={service.professional_id}
        professionalName={service.profiles?.full_name}
        context="service"
        itemTitle={service.service_name}
      />
    </div>
  );
};
